import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";

import SimpleModal from "components/SimpleModal";

import { inviteAdministrator } from "actions/employerActions";
import { createLoadingSelector, createErrorSelector } from "store/selectors";
import { employerConstants } from "actions/types";
import { toast } from "react-toastify";
// AdministratorForm is now TS; the cast below bridges its export to this
// modal's local prop interface (its InviteAdministratorValues onSubmit shape).
import AdministratorFormJs from "pages/dashboards/employerDashboard/profile/AdministratorForm";
import type { RootState } from "types/state";

interface AdministratorFormProps {
  isInviting?: boolean;
  onSubmit?: (values: InviteAdministratorValues) => void;
  error?: string;
  fixedRole?: string;
  administrators?: unknown[];
}
const AdministratorForm =
  AdministratorFormJs as React.ComponentType<AdministratorFormProps>;

/** Shape submitted by AdministratorForm's onSubmit (email/username/etc). */
type InviteAdministratorValues = Parameters<typeof inviteAdministrator>[1];

const isInvitingSelector = createLoadingSelector(
  employerConstants.INVITE_ADMINISTRATOR
);

const errorSelector = createErrorSelector(
  employerConstants.INVITE_ADMINISTRATOR
);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    isInviting: isInvitingSelector(state),
  };
};

const mapDispatchToProps = {
  inviteAdministrator,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  onClose?: (clickingOutsideModal?: boolean) => void;
  onSuccess?: () => void;
  isAdminForm?: boolean;
  administrators?: unknown[];
  companyId?: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

class InvitationModal extends React.PureComponent<Props> {
  _inviteAdmin = (values: InviteAdministratorValues) => {
    void this.props
      .inviteAdministrator(this.props.client as ApolloClient<object>, {
        ...values,
        companyId: this.props.companyId,
      })
      .then((result) => {
        if (result.success) {
          // house pattern: onSuccess invoked unconditionally; preserve
          // throw-on-undefined behavior with non-null assertion
          this.props.onSuccess!();
          toast.success("Successfully invited administrator.");
        }
      });
  };

  render() {
    const role = this.props.isAdminForm ? "Administrator" : "Agent";
    return (
      <SimpleModal
        show={true}
        // SimpleModalProps requires `buttonText` and a non-optional `onClose`,
        // but this call (with hideActions) never rendered an action button and
        // PropTypes had onClose optional — preserve the original JSX verbatim.
        // @ts-expect-error original element omits required buttonText / passes optional onClose
        onClose={this.props.onClose}
        title={`Invite ${role}`}
        showCloseButton={true}
        hideActions={true}
      >
        <AdministratorForm
          error={this.props.error}
          administrators={this.props.administrators}
          onSubmit={this._inviteAdmin}
          isInviting={this.props.isInviting}
          fixedRole={this.props.isAdminForm ? "ADMIN" : "AGENT"}
        />
      </SimpleModal>
    );
  }
}

export default connector(withApollo<Props>(InvitationModal));
